
import { mapState, mapGetters } from 'vuex';
import ReviewFormView from './components/ReviewFormView';
import ReviewCompleteView from './components/ReviewCompleteView';
import ReviewsEditLegacy from '../ReviewsEditLegacy/ReviewsEditLegacy.vue';
import ReviewAllowedAuthorType from '@/enums/ReviewAllowedAuthorType';
import ReviewFormFieldType from '@/enums/ReviewFormFieldType';
import ReviewSource from '@/enums/ReviewSource';

const getRewardableItems = (reviewForm, reviewSettings) => {
  return reviewForm.form_fields.flatMap(field => {
    switch (field.field_type) {
      case ReviewFormFieldType.REVIEW_RATING:
        return [
          {
            key: 'rating',
            value: field.review_reward_config.value
          }
        ];
      case ReviewFormFieldType.REVIEW_MESSAGE: {
        const segment = field.review_reward_config.reward_segments[0];
        const fieldConfig = field.field_config || {};
        const aiKeywordValue = fieldConfig.enable_ai_keyword
          ? (fieldConfig.ai_keywords?.length || 0) *
            reviewSettings.ai_keyword_reward_cents
          : 0;
        const aiClothingValue = fieldConfig.enable_ai_clothing_detection
          ? reviewSettings.ai_clothing_image_reward_cents
          : 0;
        return [
          { key: 'message', value: segment.base_value },
          { key: 'photo', value: segment.photo_value },
          { key: 'video', value: segment.video_value },
          ...(aiKeywordValue > 0
            ? [{ key: 'ai_keyword', value: aiKeywordValue }]
            : []),
          ...(aiClothingValue > 0
            ? [{ key: 'ai_clothing', value: aiClothingValue }]
            : [])
        ];
      }
      case ReviewFormFieldType.REVIEW_OPTION:
        return [
          {
            key: 'option',
            value: field.review_reward_config.value
          }
        ];
      default:
        throw new Error(`Undefined field_type ${field.field_type}`);
    }
  });
};

export default {
  i18nComponentKey: 'ReviewsNew.ReviewsNew',
  name: 'ReviewsNew',
  components: { ReviewFormView, ReviewCompleteView, ReviewsEditLegacy },
  data() {
    return {
      reward: 0,
      isLoading: true,
      isSaved: false,
      review: null,
      product: null,
      subOrder: null,
      reviewForm: null,
      myReviewsUrl: null,
      rewardableItems: []
    };
  },
  computed: {
    ...mapState(['api']),
    ...mapGetters(['widgetUrl']),
    ...mapState('window', ['windowService']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapGetters('widget', ['isFullscreenPopup']),
    ...mapGetters('session', ['isLoggedIn']),
    ...mapGetters('form', ['isFormDataChanged']),
    isPreview() {
      return !!this.$route.query.preview_review_form;
    },
    isEditing() {
      return !!this.$route.params.review_id;
    }
  },
  async created() {
    if (!this.reviewSettings.review_form_enabled) return;

    if (this.isPreview) this.selectReviewTarget({});
    else if (this.$route.params.review_id)
      this.selectReviewTarget({ review_id: this.$route.params.review_id });
    else {
      const params = await this.resolveReviewTargetParams();
      if (!params) {
        this.close();
        return;
      }

      this.selectReviewTarget(params);
    }
  },
  methods: {
    async resolveReviewTargetParams() {
      const {
        sub_order_id,
        sub_order_code,
        order_code,
        product_code,
        product_subcode,
        review_source
      } = this.$route.query;
      if (!this.isLoggedIn) {
        // 비회원
        if (
          this.reviewSettings.allowed_author_type !==
          ReviewAllowedAuthorType.EVERYONE
        ) {
          this.alertNoOrdersToReview();
          return null;
        }

        return Number(review_source) === ReviewSource.SMS
          ? { sub_order_id }
          : { product_code };
      }

      try {
        if (sub_order_id) {
          return { sub_order_id };
        } else if (sub_order_code || order_code) {
          // 주문 목록
          const { data } = await this.api.resolveSubOrder({
            sub_order_code,
            order_code,
            product_code,
            product_subcode
          });
          return { sub_order_id: data.sub_order_id };
        } else if (product_code) {
          // 상품 상세 페이지
          return { product_code };
        } else {
          // 리뷰 작성 유도 팝업
          const { data } = await this.api.fetchPendingReviews();
          if (data.sub_orders.length === 0) {
            this.alertNoOrdersToReview();
            return null;
          }

          return { sub_order_id: data.sub_orders[0].id };
        }
      } catch {
        this.alertNoOrdersToReview();
        return null;
      }
    },
    async onDialogClose() {
      if (this.isFormDataChanged) {
        const confirmed = await this.windowService.confirm(
          this.$t('confirm_cancel_review')
        );
        if (!confirmed) return;
      }
      this.close();
    },
    close() {
      this.windowService.closeFullscreenPopup();
    },
    onSaved({ reward }) {
      if (this.isEditing) {
        this.windowService.alert(this.$t('app.saved'));
        this.windowService.closeFullscreenPopup('saved');
        return;
      }

      this.myReviewsUrl = this.isLoggedIn
        ? this.widgetUrl('/managing_reviews/managing_reviews', {
            tab: 'written'
          })
        : this.product.url;
      this.reward = reward;
      this.isSaved = true;
    },
    selectReviewTarget(params) {
      this.isSaved = false;
      this.isLoading = true;
      const { review_source, nth_step } = this.$route.query;
      const promise = this.isPreview
        ? this.requestPreview()
        : params.review_id
        ? this.api.editReview(params.review_id)
        : this.api.newReview({ ...params, review_source, nth_step });
      promise
        .then(({ data }) => {
          const { review, product, sub_order, review_form } = data;
          this.review = review;
          this.product = product;
          this.subOrder = sub_order;
          this.reviewForm = review_form;
          this.rewardableItems = data.rewardable_items;
          this.isLoading = false;
        })
        .catch(error => {
          this.windowService.alert(error.response.data.error);
          this.close();
        });
    },
    requestPreview() {
      return new Promise(resolve => {
        this.windowService.addMessageHandler(data => {
          if (data.act !== 'update:review_form') return;

          this.$store.commit('session/SET_SESSION_PARAMS', data.member_state);
          const product = data.product || {
            id: 1,
            name: '[Sample] Orange Bucket Hat',
            is_review_content_restricted: false,
            extra_small_image_url:
              'https://assets.cre.ma/m/extra_small_sample-product1.webp'
          };
          const sub_order = {
            id: 1,
            product_options: { Color: 'Orange', Size: 'Free' },
            // en-CA로 로컬 타임존 기준 YYYY-MM-DD를 만들어 오늘 날짜로 노출 (toISOString은 UTC라 날짜가 하루 밀릴 수 있음)
            purchased_at: new Date().toLocaleDateString('en-CA'),
            purpose: 0
          };

          if (this.isLoading) {
            resolve({
              data: {
                review: { score: null, videos: [], images: [], options: [] },
                product,
                sub_order,
                review_form: data.review_form,
                rewardable_items: getRewardableItems(
                  data.review_form,
                  this.reviewSettings
                )
              }
            });
          } else {
            this.product = product;
            this.subOrder = sub_order;
            this.reviewForm = data.review_form;
            this.rewardableItems = getRewardableItems(
              data.review_form,
              this.reviewSettings
            );
          }
        });

        this.windowService.postMessage('request:review_form');
      });
    },
    alertNoOrdersToReview() {
      this.windowService.alert(this.$t('no_orders_to_review'));
    }
  }
};
